import React, {useCallback, useEffect, useState} from 'react';

import FuseSvgIcon from '@/@fuse/core/FuseSvgIcon';
import {COLORS} from '@/styles/colors';

import {
  PasswordValidation,
  PasswordValidationCodes,
  PasswordValidationRegExp,
  VerifyPasswordState,
} from './models/constants';
import {IVerifyPassword} from './models/interface';
import {VerifyPasswordStateType} from './models/types';

import './styles';

type VerifyPasswordProps = {
  newPassword: string;
  confirmPassword: string;
  verifyList: IVerifyPassword[];
  setVerify: React.Dispatch<React.SetStateAction<boolean>>;
  hideVerifyListWithoutValue?: boolean;
};

const VerifyPassword: React.FC<VerifyPasswordProps> = ({
  newPassword,
  confirmPassword,
  verifyList,
  setVerify,
  hideVerifyListWithoutValue,
}) => {
  const [localVerifyList, setLocalVerifyList] = useState(verifyList);

  //#region [---- [HOOK] ----]
  useEffect(() => {
    verifyPassword();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newPassword, confirmPassword]);
  //#endregion

  //#region [---- [LOGIC] -----]
  const validateMinLength = useCallback(() => {
    return newPassword
      ? newPassword?.length >= PasswordValidation.MIN_LENGTH
        ? VerifyPasswordState.VALID
        : VerifyPasswordState.INVALID
      : VerifyPasswordState.NONE;
  }, [newPassword]);

  const validateNumberUpperLower = useCallback(() => {
    const specialCharacterPattern = PasswordValidationRegExp.NUMBER_UPPER_LOWER;
    return newPassword
      ? specialCharacterPattern.test(newPassword)
        ? VerifyPasswordState.VALID
        : VerifyPasswordState.INVALID
      : VerifyPasswordState.NONE;
  }, [newPassword]);

  const validateSpecialCharacter = useCallback(() => {
    const specialCharacterPattern = PasswordValidationRegExp.SPECIAL_CHARACTER;
    return newPassword
      ? specialCharacterPattern.test(newPassword)
        ? VerifyPasswordState.VALID
        : VerifyPasswordState.INVALID
      : VerifyPasswordState.NONE;
  }, [newPassword]);

  const validateExactMatch = useCallback(() => {
    return newPassword
      ? newPassword === confirmPassword
        ? VerifyPasswordState.VALID
        : VerifyPasswordState.INVALID
      : VerifyPasswordState.NONE;
  }, [confirmPassword, newPassword]);

  const validateEmpty = useCallback(() => {
    return newPassword
      ? newPassword.length === newPassword.trim().length
        ? VerifyPasswordState.VALID
        : VerifyPasswordState.INVALID
      : VerifyPasswordState.NONE;
  }, [newPassword]);

  const verifyPassword = useCallback(() => {
    if (!newPassword && !confirmPassword) {
      setVerify(false);
    }
    if (verifyList) {
      const updatedVerifyList = verifyList.map(validation => {
        let newState = VerifyPasswordState.NONE;
        if (validation.isEnabled) {
          switch (validation.code) {
            case PasswordValidationCodes.MIN_LENGTH:
              newState = validateMinLength();
              break;
            case PasswordValidationCodes.NUMBER_UPPER_LOWER:
              newState = validateNumberUpperLower();
              break;
            case PasswordValidationCodes.SPECIAL_CHARACTER:
              const validateSpecialCharacterResult = validateSpecialCharacter();
              const validateEmptyResult = validateEmpty();
              newState =
                validateSpecialCharacterResult === VerifyPasswordState.VALID &&
                validateEmptyResult === VerifyPasswordState.VALID
                  ? VerifyPasswordState.VALID
                  : validateSpecialCharacterResult ===
                        VerifyPasswordState.NONE &&
                      validateEmptyResult === VerifyPasswordState.NONE
                    ? VerifyPasswordState.NONE
                    : VerifyPasswordState.INVALID;
              break;
            case PasswordValidationCodes.EXACT_MATCH:
              newState = validateExactMatch();
              break;
          }
        }

        return {
          ...validation,
          state: newState,
        };
      });

      setLocalVerifyList(updatedVerifyList);

      const allValid = !updatedVerifyList.some(
        validation => validation.state === VerifyPasswordState.INVALID,
      );
      setVerify(allValid);
    }
  }, [
    newPassword,
    confirmPassword,
    verifyList,
    setVerify,
    validateMinLength,
    validateNumberUpperLower,
    validateSpecialCharacter,
    validateEmpty,
    validateExactMatch,
  ]);

  const renderVerifyIcon = (state: VerifyPasswordStateType) => {
    switch (state) {
      case VerifyPasswordState.NONE:
        return (
          <FuseSvgIcon size={16}>heroicons-outline:minus-small</FuseSvgIcon>
        );
      case VerifyPasswordState.INVALID:
        return <FuseSvgIcon size={16}>heroicons-outline:x-mark</FuseSvgIcon>;
      case VerifyPasswordState.VALID:
        return <FuseSvgIcon size={16}>heroicons-outline:check</FuseSvgIcon>;
    }
  };

  const renderStyle = (state: VerifyPasswordStateType) => {
    switch (state) {
      case VerifyPasswordState.NONE:
        return {
          color: COLORS.ON_SURFACE_VARIANT,
        };
      case VerifyPasswordState.INVALID:
        return {
          color: COLORS.REJECT,
        };
      case VerifyPasswordState.VALID:
        return {
          color: COLORS.ON_SURFACE_1,
        };
    }
  };

  const shouldRenderList = !hideVerifyListWithoutValue || newPassword;

  if (!shouldRenderList) {
    return null;
  }
  //#endregion

  return (
    <section className="verify-password-container">
      <section className="verify-items-container">
        {localVerifyList.map(validation => (
          <p
            key={validation.code}
            className="verify-item"
            style={renderStyle(validation.state)}>
            {renderVerifyIcon(validation.state)}
            {validation.isEnabled && (
              <label
                className="verify-description"
                style={renderStyle(validation.state)}>
                {validation.description}
              </label>
            )}
          </p>
        ))}
      </section>
    </section>
  );
};

export default VerifyPassword;
